import React, { useState, useEffect } from 'react';
import { getPageBySlug } from '../lib/cms';
import { useSEO } from '../hooks/useSEO';
import FacebookFeed from '../components/FacebookFeed';

export default function Contact() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  useEffect(() => {
    async function loadData() {
      const page = await getPageBySlug('kontakt');
      setPageData(page);
      setLoading(false);
    }
    loadData();
  }, []);

  useSEO({
    title: pageData?.meta_title || 'Kontakt · Vissenbjerg Bageri',
    description: pageData?.meta_description || 'Kontakt Vissenbjerg Bageri. Ring på 64471266, send en mail til info@vissenbjergbageri.dk or besøg os på Vestergade 5 i Vissenbjerg.'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        throw new Error('Der skete en fejl ved afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus({ submitting: false, success: false, error: err.message });
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-cream">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold-brown border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-transparent text-chocolate py-12 md:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-brown">
            Skriv eller ring
          </span>
          <h1 className="font-serif text-4xl font-bold sm:text-5xl text-chocolate">
            Kontakt os
          </h1>
          <div className="h-1 w-20 bg-gold-brown mx-auto rounded-full" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Info Column */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <h2 className="font-serif text-2xl font-bold text-chocolate">
                Vi står klar til at hjælpe dig
              </h2>
              <p className="text-base text-chocolate/80 leading-relaxed">
                Har du spørgsmål og andre former for henvendelser, er du velkommen til at kontakte os pr. telefon eller via formularen her på siden. Vi vil forsøge at besvare din henvendelse så hurtigt som muligt.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-gold-brown/10 shadow-sm">
                <div className="h-10 w-10 rounded-full bg-gold-brown/10 flex items-center justify-center text-gold-brown shrink-0">
                  <i className="fa-solid fa-location-dot" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-chocolate">Adresse</h4>
                  <p className="text-sm text-chocolate/70 mt-1">Vestergade 5, 5492 Vissenbjerg</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-gold-brown/10 shadow-sm">
                <div className="h-10 w-10 rounded-full bg-gold-brown/10 flex items-center justify-center text-gold-brown shrink-0">
                  <i className="fa-solid fa-phone" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-chocolate">Telefon</h4>
                  <p className="text-sm text-chocolate/70 mt-1">
                    <a href="tel:64471266" className="hover:text-gold-brown transition-colors whitespace-nowrap">
                      64 47 12 66
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white p-5 rounded-2xl border border-gold-brown/10 shadow-sm">
                <div className="h-10 w-10 rounded-full bg-gold-brown/10 flex items-center justify-center text-gold-brown shrink-0">
                  <i className="fa-solid fa-envelope" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-chocolate">E-mail</h4>
                  <p className="text-sm text-chocolate/70 mt-1">
                    <a href="mailto:info@vissenbjergbageri.dk" className="hover:text-gold-brown transition-colors whitespace-nowrap">
                      info@vissenbjergbageri.dk
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Column */}
          <div className="lg:col-span-7 bg-white p-8 rounded-2xl shadow-md border border-gold-brown/10">
            <h3 className="font-serif text-xl font-bold text-chocolate mb-6">Send os en besked</h3>

            {status.success ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-xl space-y-3">
                <div className="flex items-center gap-2 font-bold">
                  <i className="fa-solid fa-circle-check text-xl text-emerald-600" />
                  <span>Beskeden er sendt!</span>
                </div>
                <p className="text-sm">
                  Tak for din henvendelse. Vi vender tilbage til dig så hurtigt som muligt.
                </p>
                <button
                  onClick={() => setStatus({ ...status, success: false })}
                  className="text-xs font-semibold underline hover:text-emerald-900 mt-2"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot field - absolutely hidden from humans */}
                <div className="sr-only absolute -top-9999 -left-9999">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-chocolate/70">
                      Navn <span className="text-copper">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gold-brown/20 bg-cream/20 px-4 py-3 text-sm text-chocolate focus:border-gold-brown focus:bg-white focus:outline-none transition-all"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider text-chocolate/70">
                      Telefonnummer <span className="text-copper">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gold-brown/20 bg-cream/20 px-4 py-3 text-sm text-chocolate focus:border-gold-brown focus:bg-white focus:outline-none transition-all"
                      placeholder="Dit telefonnummer"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-chocolate/70">
                    E-mailadresse <span className="text-copper">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gold-brown/20 bg-cream/20 px-4 py-3 text-sm text-chocolate focus:border-gold-brown focus:bg-white focus:outline-none transition-all"
                    placeholder="din@email.dk"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="description" className="text-xs font-semibold uppercase tracking-wider text-chocolate/70">
                    Beskrivelse / Besked <span className="text-copper">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    rows="5"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gold-brown/20 bg-cream/20 px-4 py-3 text-sm text-chocolate focus:border-gold-brown focus:bg-white focus:outline-none transition-all resize-none"
                    placeholder="Skriv din besked her..."
                  />
                </div>

                {status.error && (
                  <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-lg text-sm flex items-center gap-2">
                    <i className="fa-solid fa-circle-exclamation text-rose-600" />
                    <span>{status.error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status.submitting}
                  className="w-full rounded-lg bg-gold-brown px-6 py-3.5 text-sm font-semibold text-cream hover:bg-copper transition-colors duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
                >
                  {status.submitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-cream border-t-transparent" />
                      <span>Sender...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane" />
                      <span>Send besked</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Facebook Iframe Sektion på Kontaktsiden */}
        <div className="mt-16 pt-12 border-t border-gold-brown/15">
          <FacebookFeed />
        </div>
      </div>
    </div>
  );
}
